import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, ArrowRight, ArrowUpRight, Sparkles, Building2, ShieldCheck, Filter, Download, BarChart3, MapPin } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import FeatureTabs from "@/components/site/FeatureTabs";
import { COSEARCH_FEATURES } from "@/constants/cosearchCatalog";
import { trackEvent, GA_EVENTS } from "@/lib/analytics";

const SAMPLE = [
  { no: "12345678", name: "Acme Holdings Ltd", status: "Active", city: "London", sic: "70100" },
  { no: "11223344", name: "Northgate Group Ltd", status: "Active", city: "Leeds", sic: "26200" },
  { no: "99887766", name: "Halberd Capital LLP", status: "Active", city: "London", sic: "64999" },
  { no: "55667788", name: "Veridian Legal LLP", status: "Active", city: "London", sic: "69109" },
];

export default function CoSearchPage() {
  const [q, setQ] = useState("Acme");
  const [results, setResults] = useState(SAMPLE);

  const runSearch = (e) => {
    e?.preventDefault?.();
    const ql = (q || "").toLowerCase();
    const filtered = ql ? SAMPLE.filter((r) => (r.name + r.no + r.city).toLowerCase().includes(ql)) : SAMPLE;
    setResults(filtered);
    try {
      trackEvent(GA_EVENTS.COSEARCH_QUERY, {
        query_length: (q || "").length,
        result_count: filtered.length,
      });
    } catch (err) { /* noop */ }
  };

  return (
    <SiteLayout>
      {/* Hero with live search */}
      <section className="dark-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />

        <div className="container-cv relative pt-14 md:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> CoSearch · UK company intelligence</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-primary leading-[1.04]">
              Search 5 million UK companies.{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-accent">Instantly.</span>
                <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
              </span>
            </h1>
            <p className="mt-5 text-base md:text-[17px] text-muted-foreground leading-relaxed max-w-2xl">
              CoSearch is the UK company intelligence engine inside CoSec Global. Look up any UK company in seconds, directors, PSCs, filings, accounts, charges and ownership history, and export the report for due diligence, AML or business development.
            </p>

            {/* Search box */}
            <form onSubmit={runSearch} className="mt-7 flex flex-col sm:flex-row gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  data-testid="cosearch-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Company name or number…"
                  className="pl-11 h-12 text-base"
                />
              </div>
              <Button type="submit" data-testid="cosearch-button" className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-semibold">
                Search <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
            <p className="mt-2 text-xs text-muted-foreground">Try &quot;Acme&quot; · &quot;Halberd&quot; · &quot;12345678&quot;</p>
          </div>

          {/* Live results card */}
          <div className="lg:col-span-5">
            <div className="light-surface rounded-3xl border border-border bg-white p-5 shadow-lg" data-testid="cosearch-results">
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="font-display text-sm font-bold text-primary">Search results · {results.length}</div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-accent">Live</span>
              </div>
              <div className="mt-3 space-y-2">
                {results.map((r) => (
                  <div key={r.no} data-testid={`cosearch-row-${r.no}`} className="rounded-lg border border-border p-3 hover:border-accent/60 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-display font-bold text-primary text-sm leading-tight">{r.name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">No. {r.no} · {r.city}</div>
                      </div>
                      <span className="text-[10px] font-bold uppercase rounded-full bg-accent/15 text-primary px-2 py-1">{r.status}</span>
                    </div>
                  </div>
                ))}
                {results.length === 0 && <div className="text-sm text-muted-foreground p-4 text-center">No matches.</div>}
              </div>
              <div className="mt-4 text-[10px] text-muted-foreground">Sandbox preview, live API access on launch.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities, tabbed */}
      <div className="border-b border-border">
        <FeatureTabs
          testId="cosearch-capabilities"
          eyebrow="What CoSearch does"
          heading={<>Company data, ownership and <span className="text-accent">deal intelligence.</span></>}
          items={[
            { key: "company-profile", Icon: Building2, title: "Company profile", desc: "See status, registered address, SIC codes, accounts, filing history and charges for any of the 5m UK companies." },
            { key: "officers-pscs", Icon: ShieldCheck, title: "Officers & PSCs", desc: "Every director, secretary and Person with Significant Control, including appointment dates, resignations and overlapping roles across companies." },
            { key: "group-structure", Icon: BarChart3, title: "Group structure", desc: "Parents, subsidiaries and ownership chains visualised on an interactive tree, perfect for due diligence packs." },
            { key: "advanced-filters", Icon: Filter, title: "Advanced filters", desc: "Slice 5m UK companies by sector, region, accounts size, age and status to build targeted prospect or risk lists." },
            { key: "export-reports", Icon: Download, title: "Export reports", desc: "Branded PDF and CSV exports for DD packs, pitch decks and audit files, fully whitelabelled." },
            { key: "geocoded-data", Icon: MapPin, title: "Geocoded data", desc: "Every company plotted by registered office with regional density heatmaps, ideal for sector mapping." },
            { key: "watch-lists", Icon: Search, title: "Watch lists", desc: "Get notified by email or webhook when a watched company's status, accounts or PSCs change." },
            { key: "sector-benchmarks", Icon: BarChart3, title: "Sector benchmarks", desc: "Compare a company against its SIC code peer group on turnover, employees and growth metrics." },
          ]}
        />
      </div>

      {/* Use cases */}
      <section className="py-20 md:py-24">
        <div className="container-cv grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="section-eyebrow">Who uses CoSearch</span>
            <h2 className="mt-3 font-display text-2xl md:text-4xl font-bold text-primary tracking-tight leading-tight">Built for teams that decide on data.</h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {[
              { t: "Due diligence teams", d: "Pull a defensible UK company picture in minutes, not days." },
              { t: "Compliance & AML", d: "Verify counterparties, screen PSCs, generate evidence packs." },
              { t: "Business development", d: "Build prospect lists by sector, region, accounts size." },
              { t: "Corporate law firms", d: "Map group structures and beneficial ownership before drafting." },
            ].map((u, i) => (
              <div key={i} className="rounded-2xl border border-border bg-white p-6">
                <div className="font-display font-bold text-primary">{u.t}</div>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CoSearch feature pages grid */}
      <section className="py-20 md:py-24" data-testid="cosearch-feature-grid">
        <div className="container-cv">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div className="max-w-2xl">
              <span className="section-eyebrow">Explore CoSearch</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
                Six modules. One{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-accent">data engine.</span>
                  <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
                </span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Each feature ships as a stand-alone product page with a deep-link, demo and pricing. Click through to see what each module does.
              </p>
            </div>
            <Button asChild variant="outline" className="h-11 px-5 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
              <Link to="/contact">Talk to sales <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COSEARCH_FEATURES.map((f) => (
              <Link
                key={f.slug}
                to={`/cosearch/${f.slug}`}
                data-testid={`cosearch-feature-card-${f.slug}`}
                className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-accent group-hover:text-primary transition-colors">
                    <f.Icon className="h-5 w-5" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{f.eyebrow}</div>
                <h3 className="mt-1 font-display text-lg font-bold text-primary tracking-tight leading-snug">
                  {f.title.replace(/[-,.]$/, "")}{" "}
                  <span className="text-accent">{f.highlight}</span>
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{f.subhead}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:text-accent transition-colors">
                  Explore module <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
        <div className="container-cv max-w-4xl">
          <div className="text-center">
            <span className="section-eyebrow">FAQs</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">CoSearch questions</h2>
          </div>
          <div className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: "Where does the data come from?", a: "CoSearch ingests UK company data from the public Companies House register (Free Company Data, BulkData feeds, and the live REST API), supplemented with our own enrichment for SIC peer-benchmarks and geocoded registered offices. All sources are public and properly attributed." },
                { q: "Is this a Companies House clone?", a: "No. We use the same public source data Companies House publishes, but CoSearch adds advanced filtering, group-structure visualisation, watch lists, exportable PDF reports and integrations with CoSec App and CoVerify, capabilities Companies House doesn't offer." },
                { q: "How fresh is the data?", a: "Snapshot daily, with live API calls to Companies House for verified record fetches. Watch list alerts trigger within 4 hours of a Companies House change." },
                { q: "Can I integrate CoSearch via API?", a: "Yes. REST API access on Firm and Enterprise plans. Sandbox keys available on request." },
                { q: "Is CoSearch separate from CoSec App?", a: "Same login, same billing. CoSearch is the data layer; CoSec App is the workflow layer; CoVerify is the verification layer. Together they cover the full UK Cosec lifecycle." },
              ].map((f, i) => (
                <AccordionItem key={i} value={`cs-${i}`} data-testid={`cosearch-faq-${i + 1}`} className="rounded-xl border border-border bg-white px-5">
                  <AccordionTrigger className="text-left font-display font-bold text-primary hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              Get early access.{" "}
              <span className="text-accent">CoSearch ships soon.</span>
            </h2>
            <p className="mt-3 text-white/70 max-w-2xl">Founding users get 50% off their first year and direct access to the product team.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Request early access <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
